import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  TextField, 
  Grid, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert
} from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { TrendingUp, TrendingDown } from 'lucide-react';
import ApiFetch from '../ApiFetch';

// APIアクセス関数の定義
async function getStockInfo(code) {
    return await new ApiFetch().get(`${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/trading/stocks/${code}/info`)
      .then(r => r.json());
  }
  
  async function getPriceHistory(code, period = '1month') {
    return await new ApiFetch().get(`${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/trading/stocks/${code}/history?period=${period}`)
      .then(r => r.json());
  }
  
  async function getOrderBook(code, depth = 10) {
    return await new ApiFetch().get(`${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/trading/stocks/${code}/orderbook?depth=${depth}`)
      .then(r => r.json());
  }
  
  async function getRecentTrades(code, limit = 20) {
    return await new ApiFetch().get(`${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/trading/stocks/${code}/trades?limit=${limit}`)
      .then(r => r.json());
  }
  
  async function createOrder(orderData) {
    return await new ApiFetch().post(`${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/trading/orders/create`, orderData)
      .then(r => r.json());
  }
  
  async function cancelOrder(orderId) {
    return await new ApiFetch().post(`${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/trading/orders/cancel/${orderId}`)
      .then(r => r.json());
  }
  async function getNextPriceEstimate(code) {
    return await new ApiFetch().get(`${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/trading/stocks/${code}/next-price`)
      .then(r => r.json());
  }

  const MARKET_STATUS = {
    BEFORE_OPEN: 'BEFORE_OPEN',
    MORNING_SESSION: 'MORNING_SESSION',
    LUNCH_BREAK: 'LUNCH_BREAK',
    AFTERNOON_SESSION: 'AFTERNOON_SESSION',
    CLOSING: 'CLOSING'
  };

const StockDetailPage = () => {
  const [quantity, setQuantity] = useState('1');
  const [stockInfo, setStockInfo] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [orderBook, setOrderBook] = useState({ asks: [], bids: [] });
  const [price, setPrice] = useState('');
  const [orderType, setOrderType] = useState('market');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [marketStatus, setMarketStatus] = useState(MARKET_STATUS.MORNING_SESSION);
  const [nextPriceEstimate, setNextPriceEstimate] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  // コンポーネントの先頭で追加
  const chartContainerRef = useRef(null);
  const [chartDimensions, setChartDimensions] = useState({ width: 600, height: 300 });

  // チャートのリサイズ処理を関数化
  const updateDimensions = useCallback(() => {
    if (chartContainerRef.current) {
      const { width } = chartContainerRef.current.getBoundingClientRect();
      setChartDimensions({
        width: Math.max(width - 32, 0), // パディングを考慮し、負の値を防ぐ
        height: Math.min(300, Math.max(width * 0.5, 0)) 
      });
    }
  }, []);

  // サイズ調整用のuseEffect
  useEffect(() => {
    // 初期化時とリサイズ時に実行
    updateDimensions();

    // リサイズイベントの登録
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [loading, updateDimensions]); // loadingを依存配列に追加

 // 銘柄コードは後でルーティングから取得するように変更
 const code = '7203';
 
 useEffect(() => {
  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [stockInfoData, historyData, orderBookData] = await Promise.all([
        getStockInfo(code),
        getPriceHistory(code),
        getOrderBook(code)
      ]);
      
      setStockInfo(stockInfoData);
      setPriceHistory(historyData);
      orderBookData.asks.reverse()
      setOrderBook(orderBookData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  fetchInitialData();
}, [code]);

  useEffect(() => {
    const checkMarketStatus = () => {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const time = hours * 100 + minutes;

      if (time < 900) {
        setMarketStatus(MARKET_STATUS.BEFORE_OPEN);
        console.log(marketStatus)
      } else if (time < 1130) {
        setMarketStatus(MARKET_STATUS.MORNING_SESSION);
        console.log(marketStatus)
      } else if (time < 1230) {
        setMarketStatus(MARKET_STATUS.LUNCH_BREAK);
        console.log(marketStatus)
      } else if (time < 1510) {
        setMarketStatus(MARKET_STATUS.AFTERNOON_SESSION);
        console.log(marketStatus)
      } else {
        setMarketStatus(MARKET_STATUS.CLOSING);
        console.log(marketStatus)
      }
      //setMarketStatus(MARKET_STATUS.MORNING_SESSION)
    };

    checkMarketStatus();
    const interval = setInterval(checkMarketStatus, 1000);
    return () => clearInterval(interval);
  }, []);

  // 板情報の定期更新
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const newOrderBook = await getOrderBook(code);
        newOrderBook.asks.reverse()
        setOrderBook(newOrderBook);
      } catch (err) {
        console.error('板情報の更新に失敗:', err);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [code]);

  useEffect(() => {
    const updatePriceEstimate = async () => {
      if (marketStatus === MARKET_STATUS.BEFORE_OPEN || 
          marketStatus === MARKET_STATUS.LUNCH_BREAK) {
        try {
          const estimate = await getNextPriceEstimate(code);
          setNextPriceEstimate(estimate);
        } catch (err) {
          console.error('予想価格の取得に失敗:', err);
        }
      }
    };

    updatePriceEstimate();
    const interval = setInterval(updatePriceEstimate, 5000);
    return () => clearInterval(interval);
  }, [code, marketStatus]);

  const MarketStatusDisplay = () => {
    const getStatusText = () => {
      switch (marketStatus) {
        case MARKET_STATUS.BEFORE_OPEN:
          return '寄付前';
        case MARKET_STATUS.MORNING_SESSION:
          return '前場';
        case MARKET_STATUS.LUNCH_BREAK:
          return '昼休み';
        case MARKET_STATUS.AFTERNOON_SESSION:
          return '後場';
        case MARKET_STATUS.CLOSING:
          return '引け後';
        default:
          return '';
      }
    };

    return (
      <Typography 
        variant="body2" 
        color="text.secondary"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          mb: 1
        }}
      >
        取引状態: {getStatusText()}
        {(marketStatus === MARKET_STATUS.BEFORE_OPEN || 
          marketStatus === MARKET_STATUS.LUNCH_BREAK) && 
          nextPriceEstimate && (
          <span style={{marginLeft: 16}}>
            予想約定価格: ¥{nextPriceEstimate.price.toLocaleString()}
          </span>
        )}
      </Typography>
    );
  };

  const handleOrder = async (side) => {
    try {
      if (orderType === 'limit' && !price) {
        setSnackbar({
          open: true,
          message: '指値注文には価格の入力が必要です',
          severity: 'error'
        });
        return;
      }

      if (!quantity || parseInt(quantity) <= 0) {
        setSnackbar({
          open: true,
          message: '数量を入力してください',
          severity: 'error'
        });
        return;
      }

      const orderData = {
        code,
        side,
        order_type: orderType,
        quantity: parseInt(quantity),
        price: orderType === 'limit' ? parseFloat(price) : null,
        is_itayose: marketStatus === MARKET_STATUS.BEFORE_OPEN || 
                   marketStatus === MARKET_STATUS.LUNCH_BREAK
      };

      const result = await createOrder(orderData);
      if (result.error) {
        setSnackbar({
          open: true,
          message: result.error,
          severity: 'error'
        });
      } else {
        setSnackbar({
          open: true,
          message: `${side === 'buy' ? '購入' : '売却'}注文を受け付けました`,
          severity: 'success'
        });
        const newOrderBook = await getOrderBook(code);
        newOrderBook.asks.reverse();
        setOrderBook(newOrderBook);
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: '注文の処理に失敗しました',
        severity: 'error'
      });
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!stockInfo) return <div>データが見つかりません</div>;

  const maxVolume = Math.max(
    ...orderBook.asks.map(ask => ask.volume),
    ...orderBook.bids.map(bid => bid.volume)
  );

  const OrderBookDisplay = () => {
    // 数値フォーマット用のヘルパー関数
    const formatPrice = (price) => {
      if (price === null || price === undefined) return '---';
      return `¥${price.toLocaleString()}`;
    };

    const formatVolume = (volume) => {
      if (volume === null || volume === undefined) return '---';
      return volume.toLocaleString();
    };
    return (
    <TableContainer component={Paper} sx={{ maxWidth: 800, margin: '0 auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width="30%" align="right">気配数量</TableCell>
            <TableCell width="40%" align="center">気配値</TableCell>
            <TableCell width="30%" align="right">気配数量</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* 売り注文 */}
          {orderBook.asks.map((ask) => (
            <TableRow key={ask.price}>
              <TableCell align="right" sx={{ position: 'relative', overflow: 'hidden' }}>
                <div style={{
                  position: 'absolute',
                  right: '8px',
                  top: 0,
                  height: '100%',
                  width: `${(ask.volume / maxVolume) * 100}%`,
                  maxWidth: 'calc(100% - 16px)',
                  backgroundColor: 'rgba(244, 67, 54, 0.1)'
                }}/>
                <span style={{ position: 'relative', zIndex: 1 }}>
                  {formatVolume(ask.volume)}
                  <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                    ({ask.participants})
                  </Typography>
                </span>
              </TableCell>
              <TableCell align="center" sx={{ color: 'error.main' }}>
                  {formatPrice(ask.price)}
              </TableCell>
              <TableCell/>
            </TableRow>
          ))}
  
          {/* 板寄せ予想価格表示 */}
          <TableRow sx={{ 
            backgroundColor: orderBook.is_itayose ? 'warning.light' : 'primary.light'
          }}>
            <TableCell colSpan={3} align="center">
              <Typography variant="body1" fontWeight="bold">
                {orderBook.is_itayose ? (
                  <>
                    予想約定価格: {formatPrice(orderBook.estimated_price)}
                    <Typography variant="caption" sx={{ ml: 2 }}>
                      (予想出来高: {formatVolume(orderBook.estimated_volume)})
                    </Typography>
                  </>
                ) : (
                  `現在値: ${formatPrice(stockInfo?.current_price)}`
                )}
              </Typography>
            </TableCell>
          </TableRow>
  
          {/* 買い注文 */}
          {orderBook.bids.map((bid) => (
            <TableRow key={bid.price}>
              <TableCell/>
              <TableCell align="center" sx={{ color: 'primary.main' }}>
                {formatPrice(bid.price)}
              </TableCell>
              <TableCell align="right" sx={{ position: 'relative', overflow: 'hidden' }}>
                <div style={{
                  position: 'absolute',
                  right: '8px',
                  top: 0,
                  height: '100%',
                  width: `${(bid.volume / maxVolume) * 100}%`,
                  maxWidth: 'calc(100% - 16px)',
                  backgroundColor: 'rgba(33, 150, 243, 0.1)'
                }}/>
                <span style={{ position: 'relative', zIndex: 1 }}>
                  {formatVolume(bid.volume)}
                  <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                    ({bid.participants ?? '---'})
                  </Typography>
                </span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    )
  };
 // 数値のフォーマット用ヘルパー関数
 const formatPrice = (price) => {
    if (price === null || price === undefined) return '---';
    return `¥${price.toLocaleString()}`;
  };

  const formatNumber = (number) => {
    if (number === null || number === undefined) return '---';
    return number.toLocaleString();
  };

  const formatPriceChange = (change) => {
    if (change === null || change === undefined) return '---';
    return `¥${Math.abs(change)}`;
  };

  const formatPercentage = (percent) => {
    if (percent === null || percent === undefined) return '';
    return `(${percent}%)`;
  };
    return (
    <div className="p-4">
      <Grid container spacing={2}>
        {/* 銘柄基本情報 */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>
                  <Typography variant="h4">
                    {stockInfo?.code} {stockInfo?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stockInfo?.market || '---'}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="h3">
                    {formatPrice(stockInfo?.current_price)}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    color={stockInfo?.price_change >= 0 ? "success.main" : "error.main"}
                    display="flex"
                    alignItems="center"
                  >
                    {stockInfo?.price_change >= 0 ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
                    {formatPriceChange(stockInfo?.price_change)} {formatPercentage(stockInfo?.price_change_percent)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* チャートと取引パネル */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>株価チャート</Typography>
              <div ref={chartContainerRef}>
                <LineChart 
                  width={chartDimensions.width} 
                  height={chartDimensions.height} 
                  data={priceHistory}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis domain={['dataMin - 100', 'dataMax + 100']} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="close_price" stroke="#2196f3" />
                </LineChart>
              </div>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
        <Card>
            <CardContent>
            <Typography variant="h6" gutterBottom>取引</Typography>
            <MarketStatusDisplay />
            <Grid container spacing={2}>
                {/* 注文価格入力フィールド */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="注文価格"
                    type="number"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: <span style={{marginRight: 8}}>¥</span>
                    }}
                    onChange={(e) => setPrice(e.target.value)}
                    value={price}
                    disabled={orderType === 'market'}
                  />
                </Grid>
                {/* 注文数量入力フィールド */}
                <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="数量"
                    type="number"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                    endAdornment: <span style={{marginLeft: 8}}>株</span>
                    }}
                    onChange={(e) => setQuantity(e.target.value)}
                    value={quantity}
                />
                </Grid>
                {/* 注文タイプ選択 */}
                <Grid item xs={12}>
                <Button
                    fullWidth
                    variant={orderType === 'market' ? "contained" : "outlined"}
                    onClick={() => setOrderType('market')}
                    sx={{mb: 1}}
                >
                    成行注文
                </Button>
                <Button
                    fullWidth
                    variant={orderType === 'limit' ? "contained" : "outlined"}
                    onClick={() => setOrderType('limit')}
                >
                    指値注文
                </Button>
                </Grid>
                {/* 売買ボタン */}
                <Grid item xs={6}>
                <Button 
                    fullWidth 
                    variant="contained" 
                    color="error"
                    onClick={() => handleOrder('sell')}
                >
                    売却
                </Button>
                </Grid>
                <Grid item xs={6}>
                <Button 
                    fullWidth 
                    variant="contained" 
                    color="primary"
                    onClick={() => handleOrder('buy')}
                >
                    購入
                </Button>
                </Grid>
                {/* 概算金額表示 */}
                <Grid item xs={12}>
                <Typography variant="body2">概算金額</Typography>
                <Typography variant="h5">
                    ¥{(stockInfo.current_price * parseInt(quantity || '0')).toLocaleString()}
                </Typography>
                </Grid>
            </Grid>
            </CardContent>
        </Card>
        </Grid>

        {/* 板情報 */}
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ width: '100%', maxWidth: 1000 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom align="left">
                板情報
              </Typography>
              <OrderBookDisplay />
            </CardContent>
          </Card>
        </Grid>

        {/* 当日の値動き */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>本日の値動き</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">始値</Typography>
                  <Typography variant="h6">
                    {formatPrice(stockInfo?.open_price)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">高値</Typography>
                  <Typography variant="h6" color="error.main">
                    {formatPrice(stockInfo?.high_price)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">安値</Typography>
                  <Typography variant="h6" color="primary.main">
                    {formatPrice(stockInfo?.low_price)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">出来高</Typography>
                  <Typography variant="h6">
                    {formatNumber(stockInfo?.volume)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default StockDetailPage;